import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../Elements/Button";
import CommonModal from "../CommonModal";
import Select from "../../Elements/Select";
import { networkService } from "../../config/axios";
import UserInfoContext from "../../../utils/userInfoContext";
import apiGETRequest from "../../../utils/apiRequest";
import Input from "../../Elements/Input";
import "./myProfileModal.css";

const GEO_PROVIDER_INFO = {
    iplocate: { signupUrl: "https://www.iplocate.io/", tagline: "1000 free IP look-ups daily" },
    maxmind: { signupUrl: "https://www.maxmind.com/en/home", tagline: "Fraud detection tool" },
    ip2location: { signupUrl: "https://www.ip2location.io/pricing", tagline: "Proxy Threat Intelligence" },
};

// A native <select> shows the selected option's own label even when closed, so the
// tagline appended here is visible both in the open list and as the collapsed value --
// there's no way to show a shorter closed-state label than the option itself provides.
const GEO_PROVIDER_OPTIONS = [
    { value: "iplocate", title: `IPLocate.io — ${GEO_PROVIDER_INFO.iplocate.tagline}` },
    { value: "ip2location", title: `IP2Location — ${GEO_PROVIDER_INFO.ip2location.tagline}` },
    { value: "maxmind", title: `MaxMind — ${GEO_PROVIDER_INFO.maxmind.tagline}` },
];

const GEO_KEY_LABELS = {
    iplocate: "IPLocate.io API Key",
    ip2location: "IP2Location API Key",
    maxmind: "MaxMind License Key",
};

const MyProfileModal = ({ profileModalStaus, setProfileModalStaus, setOpenTerms }) => {
    const [timeZoneData, setTimeZoneData] = useState({});
    const [step, setStep] = useState(1);
    const [userModalField, setUserModalField] = useState({});
    // Geolocation provider config lives outside the generic field array (unlike the
    // other step2 fields) because the API key field's label and the account-id field's
    // very presence both depend on which provider is selected -- the generic array/loop
    // rendering below has no notion of one field's visibility depending on another's value.
    const [geoConfig, setGeoConfig] = useState({ provider: "", apiKey: "", accountId: "" });
    const [geoErrors, setGeoErrors] = useState({});
    const { profileData, userInfoData, setUserData, setUserProfileData, setShowNotifications } = useContext(UserInfoContext);
    const isFetchingRef = useRef(false);

    const updatingUserProfile = useCallback(async () => {
        // Prevent duplicate calls - check if already fetching
        if (isFetchingRef.current) {
            return;
        }
        
        isFetchingRef.current = true;
        try {
            let userData = await apiGETRequest(`edit.json`);
            if (userData && !userData.error) {
                setUserData(userData);
                let userProfileData = await apiGETRequest(
                    `profiles/${userData?.id}.json`
                );
                if (userProfileData && !userProfileData.error) {
                    setUserProfileData(userProfileData);
                }
            }
        } catch (error) {
            console.error("Error updating user profile:", error);
        } finally {
            isFetchingRef.current = false;
        }
    }, [setUserData, setUserProfileData]);

    const timeZoneCache = useRef(null);

    const updateTimeZone = async () => {
        if (timeZoneCache.current) {
            setTimeZoneData(timeZoneCache.current);
            return;
        }
        const getTimeZoneData = await apiGETRequest(`time-zones`);
        timeZoneCache.current = getTimeZoneData;
        setTimeZoneData(getTimeZoneData);
    };

    const handleOnchangeEvent = (element) => {
        let userModalFieldCopy = JSON.parse(JSON.stringify(userModalField));
        let valueToUpdate = userModalFieldCopy[`step${step}`].map((field) => {
            if (field.type === "name_row") {
                return {
                    ...field,
                    fields: field.fields.map((f) =>
                        f.name === element.target.name
                            ? { ...f, value: element.target.value, error: (element.target.value.trim() === "") && f.required }
                            : { ...f }
                    ),
                };
            }
            return field.name === element.target.name
                ? { ...field, value: element.target.value, error: (element.target.value.trim() === "") && field.required, errorText: undefined }
                : { ...field };
        });
        setUserModalField({
            ...userModalFieldCopy,
            [`step${step}`]: valueToUpdate,
        });
    };

    const updateModalFields = () => {
        let valueToUpdate = {
            step1: [
                {
                    type: "name_row",
                    fields: [
                        {
                            type: "text",
                            required: true,
                            label: "First Name",
                            name: "first_name",
                            value: profileData.first_name,
                        },
                        {
                            type: "text",
                            required: true,
                            label: "Last Name",
                            name: "last_name",
                            value: profileData.last_name,
                        },
                    ],
                },
                {
                    type: "select",
                    required: true,
                    label: "Time Zone",
                    name: "time_zone",
                    value: profileData.time_zone,
                },
            ],
            step2: [
                {
                    type: "text",
                    // Org members inherit the company's name and can't edit it (enforced server-side too)
                    required: !profileData.company_name_locked,
                    readOnly: !!profileData.company_name_locked,
                    label: profileData.individual ? "Business Name" : "Company Name",
                    name: "company_name",
                    value: profileData.company_name,
                },
                {
                    type: "text",
                    required: !profileData.company_website_locked,
                    readOnly: !!profileData.company_website_locked,
                    label: profileData.individual ? "Your Website" : "Company Website",
                    placeholder: "https://yourcompany.com",
                    name: "company_website",
                    value: profileData.company_website,
                },
                {
                    type: "text",
                    required: !profileData.privacy_link_locked,
                    readOnly: !!profileData.privacy_link_locked,
                    label: profileData.individual ? "Your Privacy Notice URL" : "Privacy Notice URL",
                    placeholder: "https://yourcompany.com/privacy",
                    hint: profileData.privacy_link_locked
                        ? "Your company's Privacy Notice is managed by your company admin."
                        : "Shown to people who open your LaserLINX. Must be on the same domain as your website, usually linked as \"Privacy\" in its footer.",
                    name: "privacy_link",
                    value: profileData.privacy_link,
                },
                {
                    type: "select",
                    required: true,
                    label: "Country",
                    name: "country",
                    placeholder: "Select Country",
                    options: (profileData.country_options || []).map((name) => ({ value: name, title: name })),
                    value: profileData.country || profileData.detected_country || "",
                },
                {
                    type: "text",
                    required: false,
                    label: "Direct Chat Link (Tawk.to)",
                    name: "direct_chat_link",
                    value: profileData.direct_chat_link,
                },
            ],
        };
        Object.keys(valueToUpdate).forEach((stepKey) => {
            valueToUpdate[stepKey] = valueToUpdate[stepKey].filter((field) => !field.readOnly);
        });
        setUserModalField(valueToUpdate);
        // The key/account id are never sent back down once saved, so the field always
        // starts blank -- see the geoConfig comment above and profiles_controller#update.
        setGeoConfig({
            provider: profileData.geo_provider || "",
            apiKey: "",
            accountId: profileData.geo_account_id || "",
        });
        setGeoErrors({});
    };

    const validateGeoConfig = () => {
        const errors = {};
        // Choosing a provider is optional -- location tracking works via the house
        // fallback account with nothing configured. Nothing left to validate once no
        // provider is selected.
        if (!geoConfig.provider) return errors;

        const providerUnchanged = geoConfig.provider === profileData.geo_provider;
        const keyAlreadySaved = providerUnchanged && profileData.geo_key_present;
        if (!keyAlreadySaved && !geoConfig.apiKey.trim()) errors.apiKey = true;

        if (geoConfig.provider === "maxmind") {
            const accountIdAlreadySaved = providerUnchanged && !!profileData.geo_account_id;
            if (!accountIdAlreadySaved && !geoConfig.accountId.trim()) errors.accountId = true;
        }
        return errors;
    };

    const handleMoveToNext = async () => {
        let userModalFieldCopy = JSON.parse(JSON.stringify(userModalField));
        const currentStepFields = userModalFieldCopy[`step${step}`];

        // Validate fields (including nested name_row fields)
        const hasErrors = currentStepFields.some((field) => {
            if (field.type === "name_row") {
                return field.fields.some((f) => {
                    if (f.required && !f?.value?.trim()) {
                        f.error = true;
                        return true;
                    }
                    return false;
                });
            }
            if (field.required && !field?.value?.trim()) {
                field.error = true;
                return true;
            }
            if (field.name === "time_zone" && step === 1 && !field.value) {
                field.error = true;
                return true;
            }
            return false;
        });

        // Geo provider/key/account-id live outside userModalField (see the comment on
        // geoConfig's declaration), so they're validated separately, only on the step
        // that actually renders them.
        const currentGeoErrors = step === 2 ? validateGeoConfig() : {};
        const geoHasErrors = Object.values(currentGeoErrors).some(Boolean);

        if (!hasErrors && !geoHasErrors) {
            if (step < 2) {
                setStep((prevState) => prevState + 1);
            } else {
                // Prepare data to upload — flatten name_row fields
                let dataToUpload = Object.keys(userModalFieldCopy)
                    .map((fieldID) => userModalFieldCopy[fieldID])
                    .flat()
                    .flatMap((field) => field.type === "name_row" ? field.fields : [field])
                    .reduce((acc, curr) => {
                        acc[`${curr.name}`] = curr.value;
                        return acc;
                    }, {});
                if (profileData.company_name_locked) delete dataToUpload.company_name;
                dataToUpload.geo_provider = geoConfig.provider;
                dataToUpload.geo_api_key = geoConfig.apiKey;
                dataToUpload.geo_account_id = geoConfig.accountId;
                let data, status;
                try {
                    ({ data, status } = await networkService(
                        `profiles/${userInfoData.id}.json`,
                        "PATCH",
                        { profile: { ...dataToUpload }, user_id: userInfoData.id }
                    ));
                } catch (error) {
                    const serverErrors = error?.response?.data?.errors || {};
                    let errorStep = null;
                    Object.keys(userModalFieldCopy).forEach((stepKey) => {
                        userModalFieldCopy[stepKey].forEach((field) => {
                            if (serverErrors[field.name]) {
                                field.error = true;
                                field.errorText = serverErrors[field.name].join(" ");
                                errorStep = errorStep || Number(stepKey.replace("step", ""));
                            }
                        });
                    });
                    setUserModalField(userModalFieldCopy);
                    if (errorStep) setStep(errorStep);
                    setShowNotifications({
                        showToast: true,
                        toastType: "danger",
                        toastMessage: Object.values(serverErrors).flat()[0] || "Could not save profile details. Please try again.",
                    });
                    return;
                }
                if (status === 200 && data) {
                    setUserProfileData(data);
                    setShowNotifications({
                        showToast: true,
                        toastType: "success",
                        toastMessage: "Profile details saved",
                    });
                    setOpenTerms(userInfoData?.privacy_accepted == false)
                }
                updatingUserProfile();
                setProfileModalStaus(false);
            }
        } else {
            setUserModalField(userModalFieldCopy);
            setGeoErrors(currentGeoErrors);
        }
    };

    useEffect(() => {
        if (profileModalStaus) {
            updateModalFields();
            setStep(1);
        }
    }, [profileModalStaus, profileData]);

    useEffect(() => {
        return () => {
            setStep(1);
        };
    }, []);

    useEffect(() => {
        // Only fetch if modal is open AND data is missing (to prevent duplicate calls)
        if (profileModalStaus && !userInfoData?.id) {
            updatingUserProfile();
        }
        if (profileModalStaus) {
            updateTimeZone();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileModalStaus]);

    // This modal only ever opens because something mandatory is still missing (see Header.js's
    // auto-open effect) — never as a voluntary "edit my profile" action. So while that's still
    // true, block the close button: closing without saving would let a user skip first_name/
    // privacy indefinitely via localStorage's one-time gate. The geo provider is no longer
    // part of this -- it's optional now, see Header.js.
    const isProfileIncomplete =
        (userInfoData?.privacy_accepted == null || !userInfoData?.privacy_accepted) &&
        (profileData?.first_name == null || !profileData?.first_name);

    return (
        <>
            <CommonModal
                title={"My Profile"}
                modalStatus={profileModalStaus}
                changeActiveStatus={setProfileModalStaus}
                classList="header-profile-modal-wrapper"
                closeModalOnEsc={false}
                hideCloseIcon={isProfileIncomplete}
                footer={
                    <>
                        {step == 1 ? (
                            <Button
                                classList={"success"}
                                addonClass="d-inline-flex ml-4"
                                onClick={handleMoveToNext}
                                type="submit" 
                                text={"Continue"}
                            ></Button>
                        ) : (
                            <>
                                <Button
                                    classList={"success"}
                                    addonClass="d-inline-flex ml-4 common-modal-yes-button"
                                    onClick={() => setStep((prev) => prev - 1)}
                                    text={"Back"}
                                ></Button>
                                <Button
                                    classList={"success"}
                                    addonClass="d-inline-flex ml-4 common-modal-yes-button"
                                    onClick={handleMoveToNext}
                                    type="submit" 
                                    text={"Save Changes"}
                                ></Button>
                            </>
                        )}
                    </>
                }
                centered
            >
                <form name="profileForm">
                    {userModalField[`step${step}`] != undefined &&
                        userModalField[`step${step}`].map((item, index) => (
                            <div key={`step-${step}-${index}`}>
                                {item.type === "name_row" ? (
                                    <>
                                        <div className="d-flex gap-3">
                                            {item.fields.map((f, fi) => (
                                                <div key={`name-${fi}`} style={{ flex: 1 }}>
                                                    <Input
                                                        classList=""
                                                        type={f.type}
                                                        placeholder={`Enter ${f.label}`}
                                                        name={f.name}
                                                        required={f.required}
                                                        label={f.label}
                                                        onChange={(e) => handleOnchangeEvent(e)}
                                                        value={f.value}
                                                        errorMessageStatus={f?.error != undefined ? f.error : false}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {step === 1 && (
                                            <div className="mt-3">
                                                <label className="control-label mb-2">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={userInfoData?.email || ""}
                                                    readOnly
                                                    tabIndex={-1}
                                                    onKeyDown={(e) => e.preventDefault()}
                                                    onPaste={(e) => e.preventDefault()}
                                                    style={{ backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed', userSelect: 'none' }}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : item.type === "select" ? (
                                    <>
                                        <label className="control-label mb-2">{item.required && "*"} {item?.label}</label>
                                        <Select
                                            classList=""
                                            name={item.name}
                                            required={item.required}
                                            label={item.label}
                                            placeholder={item.placeholder}
                                            optionList={item.options || Object.keys(timeZoneData).map((item) => {
                                                return {
                                                    value: timeZoneData[item].id,
                                                    title: timeZoneData[item].name,
                                                };
                                            })}
                                            onChange={(e) => handleOnchangeEvent(e)}
                                            value={item.value}
                                            errorMessageStatus={
                                                item?.error != undefined ? item.error : false
                                            }
                                        />
                                    </>
                                ) : (
                                    <Input
                                        classList=""
                                        type={item.type}
                                        placeholder={item.placeholder || `Enter ${item.label}`}
                                        name={item.name}
                                        required={item.required}
                                        label={item.label}
                                        autoComplete={item.autoComplete}
                                        readOnly={item.readOnly}
                                        title={item.readOnly ? "Set by your company account" : undefined}
                                        style={item.readOnly ? { backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' } : undefined}
                                        onChange={(e) => handleOnchangeEvent(e)}
                                        value={item.value}
                                        errorMessageStatus={
                                            item?.error != undefined ? item.error : false
                                        }
                                        errorMessage={item.errorText && <span className="text-danger">{item.errorText}</span>}
                                    >
                                        {item.hint && <p className="geo-provider-hint" style={{ marginTop: "6px" }}>{item.hint}</p>}
                                    </Input>
                                )}
                            </div>
                        ))}
                    {step === 2 && (
                        <div className="mb-3">
                            <label className="control-label mb-2">Geolocation Provider (optional)</label>
                            <p className="geo-provider-hint">
                                Includes 50 free recipient location look-ups. To keep tracking after that, pick a provider and add your own account.
                            </p>
                            <div className="geo-provider-row">
                                <div className="geo-provider-select-col">
                                    <Select
                                        classList="geo-provider-select"
                                        name="geo_provider"
                                        optionList={GEO_PROVIDER_OPTIONS}
                                        defaultValue={geoConfig.provider}
                                        onChange={(e) =>
                                            setGeoConfig((prev) => ({ ...prev, provider: e.target.value, apiKey: "", accountId: "" }))
                                        }
                                        errorMessageStatus={geoErrors.provider}
                                    />
                                </div>
                            </div>
                            {geoConfig.provider && (
                                <>
                                    <div className="geo-key-label-row">
                                        <label className="control-label">
                                            {!(geoConfig.provider === profileData.geo_provider && profileData.geo_key_present) && "*"} {GEO_KEY_LABELS[geoConfig.provider]}
                                        </label>
                                        <a
                                            href={GEO_PROVIDER_INFO[geoConfig.provider].signupUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="geo-key-signup-link"
                                        >
                                            Get a key ↗
                                        </a>
                                    </div>
                                    <Input
                                        classList="small-placeholder"
                                        type="secret"
                                        name="geo_api_key"
                                        required={
                                            !(geoConfig.provider === profileData.geo_provider && profileData.geo_key_present)
                                        }
                                        placeholder={
                                            geoConfig.provider === profileData.geo_provider && profileData.geo_key_present
                                                ? "Key saved — enter a new key to replace it"
                                                : `Paste your ${GEO_KEY_LABELS[geoConfig.provider]}`
                                        }
                                        value={geoConfig.apiKey}
                                        onChange={(e) => setGeoConfig((prev) => ({ ...prev, apiKey: e.target.value }))}
                                        autoComplete="off"
                                        errorMessageStatus={geoErrors.apiKey}
                                    />
                                    {geoConfig.provider === "maxmind" && (
                                        <Input
                                            classList=""
                                            type="text"
                                            name="geo_account_id"
                                            label="MaxMind Account ID"
                                            required={
                                                !(geoConfig.provider === profileData.geo_provider && profileData.geo_account_id)
                                            }
                                            placeholder="Enter your MaxMind Account ID"
                                            value={geoConfig.accountId}
                                            onChange={(e) => setGeoConfig((prev) => ({ ...prev, accountId: e.target.value }))}
                                            errorMessageStatus={geoErrors.accountId}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </form>
            </CommonModal>
        </>
    )
}

export default React.memo(MyProfileModal)
